import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import Subject from "../../types/core/Subject";
import { ImportLog } from "../../types/core/ImportLog";
import { unwrappedDispatch } from "../../foundation/Dispatching";

// Define the type for our state store entry
type SubjectState = {
    archives: Subject[];
    subjects: Subject[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: SubjectState = {
    archives: [],
    subjects: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const subjectSlice = createSlice({
    name: "subject",
    initialState: initialState,
    reducers: {
        resetSubjectStatus(state) {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    archives:
                        rawData?.payload?.subject?.archives?.map(
                            c => new Subject(c),
                        ) || [],
                    subjects:
                        rawData?.payload?.subject?.subjects?.map(
                            c => new Subject(c),
                        ) || [],
                    status: !rawData?.payload?.subject?.subjects
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchSubjects.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchSubjects.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.subjects = action.payload[0].map(c => new Subject(c));
                state.archives = action.payload[1].map(c => new Subject(c));
            })
            .addCase(fetchSubjects.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addSubject.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addSubject.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.subjects = state.subjects.concat(
                    action.payload.map(c => new Subject(c)),
                );
            })
            .addCase(addSubject.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateSubject.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateSubject.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const subjectIds = action.payload.map(c => c.id);
                state.subjects = state.subjects
                    .filter(c => !subjectIds.includes(c.id))
                    .concat(action.payload.map(c => new Subject(c)));
            })
            .addCase(updateSubject.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(archiveSubject.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(archiveSubject.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const subjectIds = action.payload.map(c => c.id);
                state.subjects = state.subjects.filter(
                    c => !subjectIds.includes(c.id),
                );
                state.archives = state.archives.concat(
                    action.payload.map(c => new Subject(c)),
                );
            })
            .addCase(archiveSubject.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const { resetSubjectStatus } = subjectSlice.actions;

export const initialiseSubjectDetails = createAsyncThunk(
    "subject/initialiseSubjectDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchSubjects()),
);

// This is a thunk, they are used to load the state
export const fetchSubjects = createAsyncThunk(
    "subject/fetchSubjects",
    () =>
        Promise.all([
            Datastore.getType<Subject[]>("api/Subjects"),
            Datastore.getType<Subject[]>("api/Subjects/Archive"),
        ]),
    {
        condition: (_, api) =>
            store.getState().db.subject.status === "idle",
    },
);
export const addSubject = createAsyncThunk(
    "subject/addSubject",
    (payload: Subject[]) =>
        Datastore.postType<Subject[]>("api/Subjects", payload),
    {
        condition: (_, api) =>
            store.getState().db.subject.status === "succeeded",
    },
);
export const updateSubject = createAsyncThunk(
    "subject/updateSubject",
    (payload: Subject[]) =>
        Datastore.putType<Subject[]>("api/Subjects", payload),
    {
        condition: (_, api) =>
            store.getState().db.subject.status === "succeeded",
    },
);
export const archiveSubject = createAsyncThunk(
    "subject/archiveSubject",
    (payload: Subject[]) => {
        Datastore.post(
            "api/Subjects/Archive",
            payload.map(c => c.id),
        );
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.subject.status === "succeeded",
    },
);

/*******     Importing     *******/
export const importSubjectsFromTD = async (id: number) => {
    const result = await Datastore.postType<ImportLog>(
        `api/Subjects/Import/TD/${id}`,
    );

    store.dispatch(resetSubjectStatus());
    await unwrappedDispatch(fetchSubjects());

    return result;
};

// We have to export the reducer as this is added to the state store
export default subjectSlice.reducer;
